<template>
  <div :data-testid="dataTestid" class="h-80 w-full">
    <VChart :option="option" :theme="OMR_ECHARTS_THEME" :data-testid="`${dataTestid}-chart`" autoresize class="h-full w-full" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { OMR_ECHARTS_THEME } from '@/composables/useOmrEchartsTheme'
import { VChart, type OmrEChartsOption } from '@/config/echarts'
import { makeCompact } from '@/helpers/chartFormat'

const props = withDefaults(
  defineProps<{
    categories: string[]
    barValues: number[]
    lineValues: number[]
    barColor: string
    lineColor: string
    barLabel: string
    lineLabel: string
    locale: string
    dataTestid?: string
  }>(),
  { dataTestid: 'insights-review-growth-chart' },
)

const option = computed<OmrEChartsOption>(() => {
  const compact = makeCompact(props.locale)

  return {
    tooltip: { trigger: 'axis', valueFormatter: (value) => compact(Number(value)) },
    grid: { top: 36, left: 8, right: 36, bottom: 40, containLabel: true },
    legend: { bottom: 0 },
    xAxis: { type: 'category', data: props.categories },
    yAxis: [
      {
        type: 'value',
        splitNumber: 2,
        axisLabel: { formatter: (value: number | string) => compact(Number(value)) },
      },
      {
        type: 'value',
        scale: true,
        splitLine: { show: false },
        axisLabel: { color: props.lineColor, formatter: (value: number | string) => compact(Number(value)) },
      },
    ],
    series: [
      {
        type: 'bar',
        name: props.barLabel,
        yAxisIndex: 0,
        z: 1,
        data: props.barValues,
        barWidth: '45%',
        itemStyle: { color: props.barColor, borderRadius: [4, 4, 0, 0] },
        label: { show: true, position: 'top', formatter: (params) => compact(Number(params.value)) },
      },
      {
        type: 'line',
        name: props.lineLabel,
        yAxisIndex: 1,
        z: 2,
        data: props.lineValues,
        symbol: 'circle',
        symbolSize: 7,
        lineStyle: { color: props.lineColor, width: 2 },
        itemStyle: { color: props.lineColor },
        endLabel: {
          show: true,
          color: props.lineColor,
          fontWeight: 'bold',
          formatter: (params) => compact(Number(params.value)),
        },
        labelLayout: { moveOverlap: 'shiftY' },
      },
    ],
  }
})
</script>
